import { useCallback, useEffect, useRef } from 'react';

import { GridApiPro, GridRowSelectionModel, GridRowsProp } from '@mui/x-data-grid-pro';
import { enqueueSnackbar } from 'notistack';

import { PanelGrid } from 'components/grid';
import { Block } from 'components/page';
import { useClientContext } from 'context/ClientContext';
import { ListagemProvider, useListagemContext } from 'context/ListagemContext';
import { CarrinhoProdutoGrid } from 'pages/compra/carrinho/components';
import { useCarrinhoContext } from 'pages/compra/carrinho/contexts';
import { useColumnsGrid, useQueryCarrinho, useQueryConfiguracaoMinimo } from 'pages/compra/carrinho/hooks';
import { PedidoProps } from 'pages/compra/carrinho/types';
import { brlDate } from 'util/format';

/**
 * Retorna as informações para as linhas da grid formatadas de acordo com a request do carrinho.
 */
const getRows = (data: PedidoProps[] = []): GridRowsProp =>
    data.map((item) => ({
        id: item.idPedido,
        idPedido: item.idPedido,
        nrPedido: item.nrPedido,
        dhInclusao: item.dhInclusao ? brlDate(item.dhInclusao) : null,
        dtLimitePedido: item.grade.dtLimitePedido ? brlDate(`${item.grade.dtLimitePedido}T00:00:00`) : null,
        dtEntrega: item.grade.dtEntrega ? brlDate(`${item.grade.dtEntrega}T00:00:00`) : null,
        vlTotal: item.vlTotal,
        vlFrete: item.vlFrete ? item.vlFrete : 0,
        vlTotalFrete: item.vlFrete ? item.vlTotal + item.vlFrete : item.vlTotal,
        pedidoLog: item.pedidoLog,
        tipoEntrega: item.tipoEntrega,
        usuarioPedido: item.usuarioPedido,
        pedidoStatus: item.pedidoStatus,
        loja: item.loja,
        configPedido: item.configPedido,
        fgTelaPagamento: item.fgTelaPagamento,
    }));

export function CarrinhoGrid() {
    const { disabledActions, setSelectedPedidos, selectedPedidos, setPedidos } = useCarrinhoContext();
    const { tipoPedido } = useClientContext();
    const { isFetching, data, refetch } = useQueryCarrinho();
    const { getFilterValues } = useListagemContext();
    const { idLoja: lojas = [] } = getFilterValues();
    const { data: configMinimo } = useQueryConfiguracaoMinimo();
    const { columns } = useColumnsGrid();
    const gridRef = useRef<GridApiPro>(null);
    const isLojasEqualOne = lojas.length === 1;
    const isTipoPedidoFuncionario = tipoPedido === 'funcionario';

    useEffect(() => setPedidos(data?.data || []), [data]);

    /**
     * Realiza validações de seleção dos pedidos.
     *
     * Valida se possui:
     * - apenas uma loja
     * - ao menos uma data de entrega
     * - pedidos com a mesma data de entrega
     */
    const validSelection = useCallback(
        (pedidos: PedidoProps[]) => {
            const hasMinimo = Boolean(configMinimo?.vlPesoTotalPedidoMinimo || configMinimo?.vlTotalPedidoMinimo);

            const dtEntregaPrimeiro = pedidos[0]?.grade?.dtEntrega;
            const datasIguais = pedidos.every((p) => p.grade.dtEntrega === dtEntregaPrimeiro);

            const hasPedidoTotalizador = pedidos.some((p) => p.configPedido.idTipoPedido === 4); // 4 = Totalizador
            const hasPedidoNormal = pedidos.some((p) => p.configPedido.idTipoPedido !== 4); // 4 = Totalizador
            const hasPedidoFinalizando = pedidos.some((p) => p.pedidoStatus.idPedidoStatus === 19); // 19 = Finalizando

            let message = '';

            if (!lojas.length) {
                message = 'Selecione uma loja para poder selecionar o pedido.';
            } else if (!isLojasEqualOne) {
                message = 'Selecione apenas uma loja para poder selecionar o pedido.';
            } else if (hasPedidoFinalizando) {
                message = 'Não é possivel selecionar pedidos em status finalizando.';
            } else if (hasMinimo) {
                if (hasPedidoTotalizador && hasPedidoNormal) {
                    message = 'Não é possível selecionar pedidos totalizadores junto com outros tipos de pedido.';
                } else if (pedidos.length > 0 && !dtEntregaPrimeiro && !hasPedidoTotalizador) {
                    message = 'Pedido sem data de entrega. Selecione apenas pedidos com uma data de entrega informada.';
                } else if (pedidos.length > 0 && !datasIguais) {
                    message = 'Datas de entrega diferentes. Selecione apenas pedidos com a mesma data de entrega.';
                }
            }

            if (message) {
                enqueueSnackbar(message, { variant: 'warning' });
            }

            return !message;
        },
        [lojas, configMinimo],
    );

    /**
     * Atualiza os pedidos selecionados quando uma linha é selecionada.
     */
    const onClickRow = (selectionModel: GridRowSelectionModel) => {
        if (selectionModel.ids.size === 0) {
            setSelectedPedidos([]);

            return;
        }

        const pedidos = Array.from(selectionModel.ids)
            .map((id) => data?.data?.find((pedido) => pedido.idPedido === (id as number)))
            .filter(Boolean) as PedidoProps[];

        if (validSelection(pedidos)) {
            setSelectedPedidos(pedidos);
        }
    };

    return (
        <Block>
            <PanelGrid
                ref={gridRef}
                checkboxSelection
                isTreeData
                reload={refetch}
                columns={columns}
                rows={getRows(data?.data)}
                rowsCount={data?.total || 0}
                loading={isFetching || disabledActions}
                getDetailPanelHeight={() => 'auto'}
                summary={['vlTotal', 'vlTotalFrete']}
                visibleRows={isLojasEqualOne || isTipoPedidoFuncionario ? 'all' : undefined}
                getDetailPanelContent={({ row }) => (
                    <ListagemProvider>
                        <CarrinhoProdutoGrid row={row} />
                    </ListagemProvider>
                )}
                onClickRow={onClickRow}
                selectionModel={{
                    type: 'include',
                    ids: new Set(selectedPedidos.map((pedido) => pedido.idPedido)),
                }}
            />
        </Block>
    );
}
